import React from 'react'

// Plan Types
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

import {
  Description,
  Price,
  Title,
  Wrapper,
  Input,
  PriceCents,
} from './PlanPickerCard.styles'

const CURRENCY_SYMBOL_BY_CURRENCY = {
  USD: '$',
  EUR: '€',
}

// Helper functions
const calculatePrice = (value: number, output?: 'CENTS' | 'UNITS') => {
  const price = (value / 100).toFixed(2)
  const [units, cents] = `${price}`.split('.')

  if (output === 'CENTS') {
    return cents === '00' ? '' : cents
  } else if (output === 'UNITS') {
    return units
  }

  return price.replace(/\D00$/, '')
}

interface PlanPickerCardProps<T extends object> {
  currency?: Currency
  isDisabled?: boolean
  isSelected?: boolean
  onChange: (clickedPlan: T) => void
  formatContent: (plan: T) => {
    title: string
    description: string
    price: number
    currency: Currency
    testId: string
  }
  plan: T
}

/*
 * PlanPickerCard
 *
 * Renders a single plan card with its info (plan name,
 * price per editor and monthly / yearly price).
 *
 * Cards are loaded from Apollo cache if available otherwise
 * default values are used.
 *
 */

const PlanPickerCard = <T extends object>(props: PlanPickerCardProps<T>) => {
  const { isDisabled, isSelected, onChange, plan, formatContent } = props
  const { title, description, price, currency, testId } = formatContent(plan)

  const priceUnit = calculatePrice(price, 'UNITS')
  const priceCents = calculatePrice(price, 'CENTS')

  return (
    <Wrapper>
      <Input
        type="radio"
        name="selected-plan"
        data-testid={`${testId}-radio-button`}
        disabled={isDisabled}
        checked={isSelected}
        onChange={() => onChange(plan)}
      />
      <Title>{title}</Title>
      <Price
        $currencyOrder={currency === 'USD' ? 'before' : 'after'}
        data-currency={CURRENCY_SYMBOL_BY_CURRENCY[currency]}
      >
        {priceUnit}
        {priceCents && <PriceCents>{priceCents}</PriceCents>}
      </Price>
      <Description>{description}</Description>
    </Wrapper>
  )
}

export default PlanPickerCard
